/**
 * StreamLain Notion Database Adapter
 *
 * Proprietary adapter for parsing Notion HTML exports with Relations and Attachments.
 * Uses BlockSuite utilities but does NOT modify any BlockSuite files.
 *
 * This adapter is used by the "Notion (Enhanced)" import option.
 */

import {
  getTagColor,
  ROW_SHARDED_DATABASE_GATE_MESSAGE,
} from '@blocksuite/affine/data-view';
import { nanoid } from '@blocksuite/affine/store';
import {
  AdapterTextUtils,
  HastUtils,
  type HtmlAST,
} from '@blocksuite/affine-shared/adapters';
import rehypeParse from 'rehype-parse';
import { unified } from 'unified';

// ============================================================================
// Types
// ============================================================================

export interface DatabaseColumn {
  type: string;
  name: string;
  data: {
    options?: Array<{ id: string; value: string; color: string }>;
    targetDatabaseName?: string;
  };
  id: string;
}

export interface DatabaseRow {
  [columnId: string]: {
    columnId: string;
    value: unknown;
  };
}

export interface RelationPlaceholder {
  uuid: string | null;
  name: string;
  href: string;
  targetPath: string;
}

export interface AttachmentPlaceholder {
  name: string;
  href: string;
  relativePath: string;
  isExternal: boolean;
}

export const RELATION_PLACEHOLDER_MARKER = '__notion_relation_placeholder__';
export const ATTACHMENT_PLACEHOLDER_MARKER =
  '__notion_attachment_placeholder__';

export interface ParsedDatabase {
  title: string;
  columns: DatabaseColumn[];
  rows: DatabaseRow[];
  rowIds: string[];
  /** Maps Notion UUID (32 hex chars) to AFFiNE row ID */
  notionToAffineMapping: Record<string, string>;
}

// ============================================================================
// Column Type Detection
// ============================================================================

const ColumnClassMap: Record<string, string> = {
  typesSelect: 'select',
  typesMultipleSelect: 'multi-select',
  typesNumber: 'number',
  typesCheckbox: 'checkbox',
  typesText: 'rich-text',
  typesTitle: 'title',
  typesDate: 'date',
};

const IconUrlTypeMap: Record<string, string> = {
  'arrow-northeast': 'relation',
  'arrow-up-right': 'relation',
  attachment: 'attachment',
  rollup: 'rich-text',
  formula: 'rich-text',
};

function getColumnTypeFromSvg(node: HtmlAST): string | null {
  const svg = HastUtils.querySelector(node, 'svg');
  const className = svg?.properties?.className;
  if (Array.isArray(className) && className[0]) {
    return ColumnClassMap[className[0]] ?? null;
  }
  return null;
}

function getColumnTypeFromIcon(node: HtmlAST): string | null {
  const img = HastUtils.querySelector(node, 'img');
  const src = img?.properties?.src as string | undefined;
  if (!src) return null;

  for (const [iconKey, columnType] of Object.entries(IconUrlTypeMap)) {
    if (src.includes(iconKey)) {
      return columnType;
    }
  }
  return null;
}

// ============================================================================
// Helper Functions
// ============================================================================

function querySelectorAll(node: HtmlAST, tagName: string): HtmlAST[] {
  const results: HtmlAST[] = [];

  // Handle root node from rehypeParse (type: 'root')
  const nodeType = (node as { type?: string }).type;
  if (nodeType === 'root') {
    const rootNode = node as { children?: HtmlAST[] };
    if (rootNode.children) {
      for (const child of rootNode.children) {
        results.push(...querySelectorAll(child, tagName));
      }
    }
    return results;
  }

  if (!HastUtils.isElement(node)) return results;

  if ((node as { tagName?: string }).tagName === tagName) {
    results.push(node);
  }

  const children = HastUtils.getElementChildren(node);
  for (const child of children) {
    results.push(...querySelectorAll(child, tagName));
  }

  return results;
}

function isNotionRelationLink(href: string): boolean {
  if (!href.endsWith('.html')) return false;
  if (href.startsWith('http://') || href.startsWith('https://')) return false;
  if (href.startsWith('mailto:')) return false;
  const uuidMatch = href.match(/([a-f0-9]{32})\.html$/i);
  return uuidMatch !== null;
}

// ============================================================================
// Cell Parsers
// ============================================================================

function parseRelationCell(node: HtmlAST): RelationPlaceholder[] | null {
  if (!HastUtils.isElement(node)) return null;

  const links = querySelectorAll(node, 'a');
  if (links.length === 0) return null;

  const placeholders: RelationPlaceholder[] = [];

  for (const link of links) {
    const linkElement = link as { properties?: { href?: string } };
    const rawHref = (linkElement.properties?.href as string) || '';
    const href = decodeURIComponent(rawHref);
    const name =
      HastUtils.getTextContent(
        link as Parameters<typeof HastUtils.getTextContent>[0]
      ) || '';

    if (!isNotionRelationLink(rawHref)) {
      continue;
    }

    const uuidMatch = href.match(/([a-f0-9]{32})\.html$/i);
    const uuid = uuidMatch?.[1] ?? null;
    const targetPath = href.replace(/\.html$/, '').replace(/%20/g, ' ');

    placeholders.push({ uuid, name, href, targetPath });
  }

  return placeholders.length > 0 ? placeholders : null;
}

function parseAttachmentCell(node: HtmlAST): AttachmentPlaceholder[] | null {
  if (!HastUtils.isElement(node)) return null;

  const links = querySelectorAll(node, 'a');
  if (links.length === 0) return null;

  const attachments: AttachmentPlaceholder[] = [];

  for (const link of links) {
    const linkElement = link as { properties?: { href?: string } };
    const rawHref = (linkElement.properties?.href as string) || '';
    const href = decodeURIComponent(rawHref);
    const name =
      HastUtils.getTextContent(
        link as Parameters<typeof HastUtils.getTextContent>[0]
      ) || '';

    let relativePath = href;
    let isExternal = false;
    if (href.startsWith('http://') || href.startsWith('https://')) {
      isExternal = true;
      relativePath = '';
    }

    attachments.push({ name, href, relativePath, isExternal });
  }

  return attachments.length > 0 ? attachments : null;
}

// ============================================================================
// Main Parser
// ============================================================================

/**
 * Parse a Notion HTML table into a structured database.
 *
 * @param tableNode - The HTML AST node for the <table> element
 * @returns Parsed database with columns, rows, and UUID mapping
 */
export function parseNotionTable(tableNode: HtmlAST): ParsedDatabase | null {
  if (!HastUtils.isElement(tableNode)) return null;
  if ((tableNode as { tagName?: string }).tagName !== 'table') return null;

  const columns: DatabaseColumn[] = [];
  const rows: DatabaseRow[] = [];
  const notionToAffineMapping: Record<string, string> = {};
  const rowNotionUuids: (string | null)[] = [];

  // Find thead and tbody
  const thead = HastUtils.querySelector(tableNode, 'thead');
  const tbody = HastUtils.querySelector(tableNode, 'tbody');

  // Parse columns from <th> elements
  if (thead) {
    const thElements = querySelectorAll(thead, 'th');
    for (const th of thElements) {
      const columnId = nanoid();
      const columnName = HastUtils.getTextContent(
        HastUtils.getTextChildrenOnlyAst(
          th as Parameters<typeof HastUtils.getTextChildrenOnlyAst>[0]
        )
      );

      // Detect column type
      let columnType = getColumnTypeFromSvg(th);
      if (!columnType) {
        columnType = getColumnTypeFromIcon(th);
      }
      if (!columnType) {
        columnType = 'rich-text';
      }

      columns.push({
        type: columnType,
        name: columnName,
        data: Object.create(null),
        id: columnId,
      });
    }
  }

  // Parse rows from <tr> elements in tbody
  if (tbody) {
    const trElements = querySelectorAll(tbody, 'tr');

    for (const tr of trElements) {
      // Extract Notion UUID from tr id attribute
      const trId = (tr as { properties?: { id?: string } }).properties?.id;
      const notionUuid = trId?.replace(/-/g, '') || null;
      rowNotionUuids.push(notionUuid);

      const row: DatabaseRow = {};
      const tdElements = HastUtils.getElementChildren(tr);

      tdElements.forEach((td, index) => {
        const column = columns[index];
        if (!column) return;

        // Check for .cell-title (title column)
        if (HastUtils.querySelector(td, '.cell-title')) {
          column.type = 'title';
          row[column.id] = {
            columnId: column.id,
            value: HastUtils.getTextContent(td),
          };
          return;
        }

        const optionIds: string[] = [];

        // Parse based on cell content
        if (HastUtils.querySelector(td, 'time')) {
          // Date cell
          const timeElement = HastUtils.querySelector(td, 'time');
          let rawData = HastUtils.getTextContent(timeElement).trim();
          if (rawData.startsWith('@')) {
            rawData = rawData.slice(1);
          }
          const timestamp = new Date(rawData).getTime();

          if (!Number.isNaN(timestamp)) {
            column.data = {};
            if (column.type !== 'date') {
              column.type = 'date';
            }
            row[column.id] = { columnId: column.id, value: timestamp };
          } else {
            row[column.id] = {
              columnId: column.id,
              value: HastUtils.getTextContent(td),
            };
          }
        } else if (HastUtils.querySelector(td, '.selected-value')) {
          // Select/Multi-select cell
          if (!('options' in column.data)) {
            column.data.options = [];
          }
          if (!['multi-select', 'select'].includes(column.type)) {
            column.type = 'select';
          }
          if (
            column.type === 'select' &&
            td.type === 'element' &&
            td.children.length > 1
          ) {
            column.type = 'multi-select';
          }

          if (td.type === 'element') {
            td.children.forEach(span => {
              const value = HastUtils.getTextContent(span);
              const existing = column.data.options?.find(
                o => o.value === value
              );
              const id = existing?.id ?? nanoid();
              if (!existing) {
                column.data.options?.push({
                  id,
                  value,
                  color: getTagColor(),
                });
              }
              optionIds.push(id);
            });
          }
          row[column.id] = { columnId: column.id, value: optionIds };
        } else if (HastUtils.querySelector(td, '.checkbox')) {
          // Checkbox cell
          if (column.type !== 'checkbox') {
            column.type = 'checkbox';
          }
          const isChecked = !!HastUtils.querySelector(td, '.checkbox-on');
          row[column.id] = { columnId: column.id, value: isChecked };
        } else if (column.type === 'attachment') {
          // Attachment cell
          const attachments = parseAttachmentCell(td);
          if (attachments && attachments.length > 0) {
            row[column.id] = {
              columnId: column.id,
              value: {
                [ATTACHMENT_PLACEHOLDER_MARKER]: true,
                placeholders: attachments,
              },
            };
          } else {
            row[column.id] = { columnId: column.id, value: {} };
          }
        } else if (column.type === 'relation' || parseRelationCell(td)) {
          // Relation cell
          const relations = parseRelationCell(td);
          if (relations && relations.length > 0) {
            if (column.type !== 'relation') {
              column.type = 'relation';
            }
            // Extract target database name from first placeholder
            const firstPlaceholder = relations[0];
            if (firstPlaceholder) {
              const pathParts = firstPlaceholder.targetPath.split('/');
              if (pathParts.length > 1 && !column.data.targetDatabaseName) {
                column.data.targetDatabaseName = decodeURIComponent(
                  pathParts[0] ?? ''
                );
              }
            }
            row[column.id] = {
              columnId: column.id,
              value: {
                [RELATION_PLACEHOLDER_MARKER]: true,
                placeholders: relations,
              },
            };
          } else {
            row[column.id] = { columnId: column.id, value: [] };
          }
        } else if (column.type === 'number') {
          // Number cell
          const text = HastUtils.getTextContent(td);
          const number = Number(text);
          if (Number.isNaN(number)) {
            column.type = 'rich-text';
            row[column.id] = {
              columnId: column.id,
              value: AdapterTextUtils.createText(text),
            };
          } else {
            row[column.id] = { columnId: column.id, value: number };
          }
        } else {
          // Default: text
          const text = HastUtils.getTextContent(td);
          row[column.id] = { columnId: column.id, value: text };
        }

        // Convert to rich-text if needed
        if (
          column.type === 'rich-text' &&
          !AdapterTextUtils.isText(row[column.id]?.value)
        ) {
          row[column.id] = {
            columnId: column.id,
            value: AdapterTextUtils.createText(
              String(row[column.id]?.value ?? '')
            ),
          };
        }
      });

      rows.push(row);
    }
  }

  // Build notionToAffineMapping and assign row IDs
  const rowsWithIds: Array<{ id: string; row: DatabaseRow }> = [];
  rows.forEach((row, index) => {
    const rowId = nanoid();
    const notionUuid = rowNotionUuids[index];
    if (notionUuid) {
      notionToAffineMapping[notionUuid] = rowId;
    }
    rowsWithIds.push({ id: rowId, row });
  });

  // Expand select values (single select should be single value, not array)
  rowsWithIds.forEach(({ row }) => {
    Object.keys(row).forEach(columnId => {
      const cell = row[columnId];
      const column = columns.find(c => c.id === columnId);
      if (column?.type === 'select' && Array.isArray(cell?.value)) {
        cell.value = (cell.value as string[])[0];
      }
    });
  });

  // Get database title
  let title = '';
  const parent = (tableNode as { parent?: HtmlAST }).parent;
  if (parent && HastUtils.isElement(parent)) {
    const titleElement = HastUtils.querySelector(parent, '.collection-title');
    if (titleElement) {
      title = HastUtils.getTextContent(titleElement);
    }
  }

  return {
    title,
    columns,
    rows: rowsWithIds.map(r => r.row),
    rowIds: rowsWithIds.map(r => r.id),
    notionToAffineMapping,
  };
}

/**
 * Type guard for relation placeholder values
 */
export function isRelationPlaceholder(value: unknown): value is {
  [RELATION_PLACEHOLDER_MARKER]: true;
  placeholders: RelationPlaceholder[];
} {
  return (
    typeof value === 'object' &&
    value !== null &&
    RELATION_PLACEHOLDER_MARKER in value
  );
}

/**
 * Type guard for attachment placeholder values
 */
export function isAttachmentPlaceholder(value: unknown): value is {
  [ATTACHMENT_PLACEHOLDER_MARKER]: true;
  placeholders: AttachmentPlaceholder[];
} {
  return (
    typeof value === 'object' &&
    value !== null &&
    ATTACHMENT_PLACEHOLDER_MARKER in value
  );
}

// ============================================================================
// HTML Parsing
// ============================================================================

/**
 * Parse HTML string to HtmlAST
 */
function parseHtmlToAst(html: string): HtmlAST {
  return unified().use(rehypeParse).parse(html) as HtmlAST;
}

/**
 * Find all table elements in HTML AST
 */
function findTables(node: HtmlAST): HtmlAST[] {
  return querySelectorAll(node, 'table');
}

/**
 * Parse all databases from an HTML string.
 * Returns an array of parsed databases (typically one per HTML file).
 */
export function parseNotionHtml(html: string): ParsedDatabase[] {
  const ast = parseHtmlToAst(html);
  const tables = findTables(ast);
  const results: ParsedDatabase[] = [];

  for (const table of tables) {
    const parsed = parseNotionTable(table);
    if (parsed) {
      results.push(parsed);
    }
  }

  return results;
}

/**
 * Parse HTML and convert to BlockSnapshot format.
 * This is the main entry point for the DatabaseExtractor.
 */
export function parseNotionHtmlToBlockSnapshot(
  html: string
): DatabaseBlockSnapshot | null {
  const databases = parseNotionHtml(html);
  if (databases.length === 0) return null;

  // Use the first database (typically one per HTML file)
  return convertToBlockSnapshot(databases[0]);
}

// ============================================================================
// BlockSnapshot Conversion
// ============================================================================

export interface DatabaseBlockSnapshot {
  type: 'block';
  id: string;
  flavour: 'affine:database';
  props: {
    databaseId: string;
    databaseStorageVersion: number;
    views: Array<{
      id: string;
      name: string;
      mode: string;
      columns: unknown[];
      filter: { type: string; op: string; conditions: unknown[] };
      header: { titleColumn: string; iconColumn: string };
    }>;
    title: {
      '$blocksuite:internal:text$': true;
      delta: Array<{ insert: string }>;
    };
    columns: DatabaseColumn[];
    cells: Record<string, DatabaseRow>;
    notionToAffineMapping: Record<string, string>;
  };
  children: unknown[];
}

/**
 * Convert ParsedDatabase to BlockSnapshot format.
 * This allows seamless integration with the existing DatabaseExtractor flow.
 */
export function convertToBlockSnapshot(
  parsed: ParsedDatabase
): DatabaseBlockSnapshot {
  const titleColumn = parsed.columns.find(c => c.type === 'title');
  if (!titleColumn) {
    throw new Error(
      `Notion database "${parsed.title || 'Untitled'}" is missing a title column`
    );
  }

  throw new Error(
    `Notion database snapshot conversion is not a product import path; use the local sharded database import writer. ${ROW_SHARDED_DATABASE_GATE_MESSAGE}.`
  );
}
